import { useState, useMemo, useCallback, useRef, useEffect, createContext, useContext } from "react";
import { displayName, marketUrl, fmtUsd, timeAgo } from "./lib/format.js";
import {
  normaliseAlert, normaliseTrader, normaliseTrade, filterVisibleAlerts,
  normalisePosition, normaliseClosedPosition, normalisePositionTrade,
} from "./lib/alerts.js";
import { useAlertStream } from "./lib/alertStream.js";
import ValueChart from "./components/ValueChart.jsx";

// ════════════════════════════════════════════════════════════════════
// Follow the Leader — App Shell (API-connected)
// Onboarding → Priming gate (linear, one-time), then tabbed app:
// Alerts · Leaderboard · Favorites · Settings.
//
// API INTEGRATION NOTES:
//   Base URL is read from import.meta.env.VITE_API_URL (set in .env)
//   or falls back to http://localhost:8000.
//   When the backend is unreachable, all tabs fall back to mock data
//   so the UI stays usable during development.
//
//   WebSocket: ws(s)://host/ws?device_id=<id>
//   REST:
//     GET  /alerts?device_id=&limit=&conviction_min=&win_rate_min=&top_n=
//     GET  /leaderboard?window=day|week|all&limit=
//     GET  /traders/{address}/positions (open)
//     GET  /traders/{address}/closed-positions
//     POST /devices/register  { device_id, platform }
//     GET  /devices/{id}/settings
//     PUT  /devices/{id}/settings  { conviction_multiplier, win_rate_floor,
//                                    top_n, fav_bypass_rank, fav_bypass_filters,
//                                    notif_enabled, quiet_from, quiet_to }
// ════════════════════════════════════════════════════════════════════

const API_BASE = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "http://localhost:8000";
const WS_BASE  = API_BASE.replace(/^http/, "ws");

// Stable device ID persisted in localStorage
function getDeviceId() {
  const key = "ftl_device_id";
  let id = localStorage.getItem(key);
  if (!id) { id = `web-${crypto.randomUUID()}`; localStorage.setItem(key, id); }
  return id;
}

const DEVICE_ID = getDeviceId();

// ── theme tokens (Sage) ──────────────────────────────────────────────
// Two palettes; the active one is picked by ThemeContext's `mode`.
const LIGHT = {
  bg:        "#DDE8DC",
  bg2:       "#D4E0D3",
  surface:   "#EBF2EA",
  surface2:  "#F4F8F4",
  border:    "#B0C8AE",
  border2:   "#C8DCC6",
  green:     "#2A5E30",
  greenDim:  "#3A7A40",
  text:      "#141E14",
  textMuted: "#507050",
  textDim:   "#7A9078",
  amber:     "#7A5000",
  amberDim:  "#C8900018",
  red:       "#8A2020",
  redDim:    "#8A202018",
  navBg:     "#2A5E30",
  navText:   "#FFFFFF",
  navMuted:  "rgba(255,255,255,0.6)",
  headerBg:  "#C8DCC6",
};
const DARK = {
  bg:        "#0A1210",
  bg2:       "#0C1610",
  surface:   "#0F1A10",
  surface2:  "#152018",
  border:    "#243028",
  border2:   "#1E2E20",
  green:     "#5EC870",
  greenDim:  "#1F4A22",
  text:      "#DEEADE",
  textMuted: "#6A8A6A",
  textDim:   "#4A6A4A",
  amber:     "#C8A040",
  amberDim:  "#C8A04018",
  red:       "#E05050",
  redDim:    "#E0505018",
  navBg:     "#1F4A22",
  navText:   "#C8EAC8",
  navMuted:  "rgba(160,210,160,0.6)",
  headerBg:  "#162A18",
};

const THEME_STORAGE_KEY = "ftl_theme";

function getInitialThemeMode() {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

const ThemeContext = createContext({ mode: "dark", toggle: () => {} });

// Wraps the app (and, via main.jsx, the SupportWidget sitting alongside it)
// so both share one toggle. Persists to localStorage; falls back to the
// OS preference only on first load / when nothing's been saved yet.
export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(getInitialThemeMode);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function useThemeMode() {
  return useContext(ThemeContext);
}

// The hook every color-consuming component calls instead of reading a
// static C object — returns the active palette for the current mode.
export function useTheme() {
  const { mode } = useContext(ThemeContext);
  return mode === "light" ? LIGHT : DARK;
}

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// ── Rank filter constants ───────────────────────────────────────────
const RANK_MAX   = 200;
const RANK_CHIPS = [5, 10, 25, 50, 100, 200];

// ── Alert category tagging ────────────────────────────────────────────
// Fixed system thresholds mirroring apps/api/core.py's WHALE_CATEGORY_USD_FLOOR /
// DORMANCY_CATEGORY_DAYS — display-only, never a per-device gate.
const WHALE_CATEGORY_USD_FLOOR = 25000;
const DORMANCY_CATEGORY_DAYS   = 5.0;

// ════════════════════════════════════════════════════════════════════
// MOCK DATA — fallback when backend is unreachable
// ════════════════════════════════════════════════════════════════════
const MOCK_TRADERS = [
  { id:"0x9a4f2c8e1b7d3a6f9c0e4b2d8a1f7c3e6b9d0a2f", rank:1, pnlPeriod:184200, volPeriod:1240000, pnlDay:184200, pnlAll:2140000, winRate:0.71, resolved:42 },
  { id:"0x3e7b1d9a0c5f8e2b4d6a9c1f3e7b0d8a2c5f9e1b", rank:2, pnlPeriod:142800, volPeriod:980000,  pnlDay:142800, pnlAll:2140000, winRate:0.64, resolved:28 },
  { id:"0x1c8d4a2f6e9b0d3a7c5f1e8b2d4a6c9f0e3b7d5a", rank:3, pnlPeriod:96400,  volPeriod:610000,  pnlDay:96400,  pnlAll:410000,  winRate:0.58, resolved:7  },
  { id:"0x5f2e9c1a8d4b6f0e3c7a9d1f5b8e2c4a6d0f3b9e", rank:4, pnlPeriod:71900,  volPeriod:480000,  pnlDay:71900,  pnlAll:980000,  winRate:0.67, resolved:31 },
  { id:"0x8b3d6f1e9a2c4d7b0f5e8a1c3d6b9f2e4a7c0d5b", rank:5, pnlPeriod:53100,  volPeriod:340000,  pnlDay:53100,  pnlAll:220000,  winRate:0.55, resolved:19 },
  { id:"0x2a7c0e5b8d1f4a9c6e3b0d7f2a5c8e1b4d9f6a3c", rank:6, pnlPeriod:38700,  volPeriod:260000,  pnlDay:38700,  pnlAll:150000,  winRate:0.62, resolved:24 },
];
const NOW = Date.now();
const MOCK_ALERTS = [
  { txHash:"0xaa01", trader:MOCK_TRADERS[0].id, rank:1, winRate:0.71, resolved:42, side:"BUY",  outcome:"YES", market:"Will the Fed cut rates in September?",       slug:"fed-cut-september",  usd:48200, price:0.61, conviction:3.2, ts:NOW-90e3        },
  { txHash:"0xaa02", trader:MOCK_TRADERS[1].id, rank:2, winRate:0.64, resolved:28, side:"BUY",  outcome:"NO",  market:"Will BTC close above $100k this quarter?",  slug:"btc-100k-q3",        usd:22500, price:0.44, conviction:1.1, ts:NOW-14*60e3     },
  { txHash:"0xaa03", trader:MOCK_TRADERS[2].id, rank:3, winRate:0.58, resolved:7,  side:"SELL", outcome:"YES", market:"Will Team A win the championship?",          slug:"team-a-championship", usd:31000, price:0.55, conviction:2.4, ts:NOW-52*60e3     },
  { txHash:"0xaa04", trader:MOCK_TRADERS[1].id, rank:2, winRate:0.64, resolved:28, side:"BUY",  outcome:"YES", market:"Will inflation come in under 3% next month?",slug:"inflation-under-3",  usd:12800, price:0.39, conviction:null,ts:NOW-3*3600e3    },
  { txHash:"0xaa05", trader:MOCK_TRADERS[3].id, rank:4, winRate:0.67, resolved:31, side:"BUY",  outcome:"YES", market:"Will the merger be approved by Q3?",         slug:"merger-q3",          usd:67400, price:0.72, conviction:4.1, ts:NOW-6*3600e3    },
];
const MOCK_OPEN = [
  { id:"c1", market:"Will the Fed cut rates in September?",      slug:"fed-cut-september", outcome:"YES", currentValue:48200, positionSizeUsd:44000, price:0.61 },
  { id:"c2", market:"Will BTC close above $100k this quarter?",  slug:"btc-100k-q3",       outcome:"NO",  currentValue:22500, positionSizeUsd:19000, price:0.44 },
];
const MOCK_CLOSED = [
  { id:"r1", market:"Will inflation come in under 3% in June?",  slug:"inflation-under-3-june", outcome:"YES", pnl:31400,  positionSizeUsd:40000, won:true,  at:"2026-06-12" },
  { id:"r2", market:"Will the incumbent win the primary?",       slug:"incumbent-primary",       outcome:"NO",  pnl:-12800, positionSizeUsd:25000, won:false, at:"2026-06-08" },
  { id:"r3", market:"Will the merger be approved by Q2?",        slug:"merger-q2",               outcome:"YES", pnl:8600,   positionSizeUsd:18000, won:true,  at:"2026-05-30" },
];

// ════════════════════════════════════════════════════════════════════
// API LAYER
// All calls return { data, error, fromMock }.
// Components check `fromMock` to optionally show a "live data unavailable"
// banner rather than crashing.
// ════════════════════════════════════════════════════════════════════
async function apiFetch(path, opts = {}) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { "Content-Type": "application/json" },
      ...opts,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return { data: await res.json(), error: null, fromMock: false };
  } catch (err) {
    return { data: null, error: err.message, fromMock: false };
  }
}

// Register device on first load (fire-and-forget)
async function registerDevice() {
  await apiFetch("/devices/register", {
    method: "POST",
    body: JSON.stringify({ device_id: DEVICE_ID, platform: "web" }),
  });
}

async function fetchAlerts({
  deviceId, convictionMin, winRateMin, topN, rankWindow = "day", limit = 50,
  whaleUsdThreshold = 0, dormancyThresholdDays = 0, excludedCategories = "", category = "",
}) {
  const params = new URLSearchParams({
    device_id:      deviceId,
    limit,
    conviction_min: convictionMin,
    win_rate_min:   winRateMin,
    top_n:          topN,
    rank_window:    rankWindow,
  });
  if (whaleUsdThreshold > 0)     params.set("whale_usd_threshold", whaleUsdThreshold);
  if (dormancyThresholdDays > 0) params.set("dormancy_threshold_days", dormancyThresholdDays);
  if (excludedCategories)        params.set("excluded_categories", excludedCategories);
  if (category)                  params.set("category", category);
  const { data, error } = await apiFetch(`/alerts?${params}`);
  if (error || !data) return { data: MOCK_ALERTS.map(normaliseAlert), fromMock: true };
  return { data: (data.alerts ?? data).map(normaliseAlert), fromMock: false };
}

// Dedup by id — prevents "two children with the same key" React warnings
// in the trader list (key={t.id}). Ranks can shift between the backend's
// paginated offset fetches (core.py's _fetch_leaderboard_page) for a
// fast-moving leaderboard, occasionally letting one trader land on two
// adjacent pages. iOS's fetchLeaderboard (lib/api.ts) already guards
// against this — this mirrors that fix for web.
function dedupeTraders(traders) {
  const seen = new Set();
  return traders.filter((t) => {
    if (!t.id || seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

async function fetchLeaderboard(window_ = "day") {
  const { data, error } = await apiFetch(`/leaderboard?window=${window_}&limit=200`);
  if (error || !data) return { data: MOCK_TRADERS.map(normaliseTrader), fromMock: true };
  return { data: dedupeTraders((data.traders ?? data).map(normaliseTrader)), fromMock: false };
}

async function fetchOpenPositions(address) {
  const { data, error } = await apiFetch(`/traders/${address}/positions`);
  if (error || !data) return { data: MOCK_OPEN, fromMock: true };
  return { data: (data.positions ?? data).map(normalisePosition), fromMock: false };
}

// win_rate/resolved are computed server-side from this same page of closed
// positions — /leaderboard always returns null/0 for those (see backend
// core.py), so this is the only place a real number comes from.
async function fetchClosedPositions(address) {
  const { data, error } = await apiFetch(`/traders/${address}/closed-positions`);
  if (error || !data) return { data: MOCK_CLOSED, winRate: null, resolved: 0, fromMock: true };
  return {
    data:     (data.positions ?? data).map(normaliseClosedPosition),
    winRate:  data.win_rate  ?? null,
    resolved: data.resolved  ?? 0,
    fromMock: false,
  };
}

// Buy/sell fill timeline for a single position — lazy-loaded when a closed
// position card is expanded, so a big realizedPnl with no matching "open
// position" is explainable (when it was entered, whether sold early).
async function fetchPositionTimeline(address, conditionId) {
  const { data, error } = await apiFetch(`/traders/${address}/positions/${conditionId}/trades`);
  if (error || !data) return { data: [], fromMock: true };
  return { data: (data.trades ?? data).map(normalisePositionTrade), fromMock: false };
}

async function fetchWalletValue(address) {
  const { data, error } = await apiFetch(`/traders/${address}/value`);
  if (error || !data) return { data: 0, fromMock: true };
  return { data: data.value ?? 0, fromMock: false };
}

async function fetchTradeHistory(address, { limit = 20, offset = 0, convictionMin = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (convictionMin > 0) params.set("conviction_min", convictionMin);
  const { data, error } = await apiFetch(`/traders/${address}/activity?${params}`);
  if (error || !data) return { data: [], fromMock: true, hasMore: false };
  const trades = (data.trades ?? data).map(normaliseTrade);
  // has_more comes from the server (based on the raw, pre-filter page size),
  // not trades.length === limit — the conviction filter can legitimately
  // shrink a full raw page to fewer (or zero) qualifying trades.
  return { data: trades, fromMock: false, hasMore: data.has_more ?? false };
}

async function fetchValueHistory(address, days = 30) {
  const { data, error } = await apiFetch(`/traders/${address}/value-history?days=${days}`);
  if (error || !data) return { data: [], fromMock: true };
  return { data: data.points ?? data, fromMock: false };
}

async function fetchSettings(deviceId) {
  const { data, error } = await apiFetch(`/devices/${deviceId}/settings`);
  if (error || !data) return { data: null, fromMock: true };
  return { data, fromMock: false };
}

async function saveSettings(deviceId, patch) {
  return apiFetch(`/devices/${deviceId}/settings`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

// ════════════════════════════════════════════════════════════════════
// WEBSOCKET HOOK
// Reconnects with back-off on unexpected close. Dedupes by txHash.
// ════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════
// SHARED UI ATOMS
// ════════════════════════════════════════════════════════════════════
function OutcomePill({ o }) {
  const C = useTheme();
  const yes = o === "YES";
  return (
    <span style={{ fontSize:11, fontWeight:700, color:yes?C.green:C.red, background:yes?C.greenDim:C.redDim, border:`1px solid ${(yes?C.green:C.red)}44`, borderRadius:5, padding:"1px 6px" }}>{o}</span>
  );
}

// Buy/sell indicator, styled as a small badge (green text on green+18 alpha
// background for buys, red on red+18 for sells) rather than plain text.
function SideBadge({ side }) {
  const C = useTheme();
  const buy = side === "BUY";
  const color = buy ? C.green : C.red;
  return (
    <span style={{ fontSize:11, fontWeight:700, color, background:`${color}18`, borderRadius:5, padding:"1px 6px" }}>
      {buy ? "Bought" : "Sold"}
    </span>
  );
}

function WinRate({ winRate, resolved, showSmall = true }) {
  const C = useTheme();
  if (winRate == null) return null;
  const pct   = Math.round(winRate * 100);
  const small = resolved < 10;
  return (
    <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
      <span style={{ color: pct >= 60 ? C.green : C.textMuted }}>{pct}% win</span>
      {small && showSmall && <span title={`Only ${resolved} resolved — small sample.`} style={{ color:C.amber, fontSize:10.5, fontWeight:600, cursor:"help" }}>small sample</span>}
    </span>
  );
}

function MarketLink({ slug, eventSlug, children, color }) {
  const C = useTheme();
  const linkColor = color ?? C.textMuted;
  const url = marketUrl(slug, eventSlug);
  if (!url) return <span>{children}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color:linkColor, textDecoration:"none" }}>
      {children}<span style={{ color:C.textMuted, marginLeft:4, fontSize:12 }} aria-hidden="true">↗</span>
    </a>
  );
}

function Footer({ children }) {
  const C = useTheme();
  return <p style={{ fontSize:11.5, color:C.textMuted, textAlign:"center", marginTop:24, opacity:0.7, lineHeight:1.5 }}>{children}</p>;
}

function MockBanner() {
  const C = useTheme();
  return (
    <div style={{ background:C.amberDim, border:`1px solid ${C.amber}44`, borderRadius:10, padding:"8px 12px", marginBottom:14, fontSize:12, color:C.amber, display:"flex", alignItems:"center", gap:8 }}>
      <span>⚠</span>
      <span>Backend unreachable — showing mock data. Start the API server at <code style={{ fontSize:11 }}>{API_BASE}</code>.</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// GATE SCREENS
// ════════════════════════════════════════════════════════════════════
function Onboarding({ onContinue }) {
  const C = useTheme();
  const [ack, setAck] = useState(false);
  const features = [
    ["Live alerts",        "Fresh trades, not stale leaderboard data.",                                0.45],
    ["Conviction scoring", "See how a bet compares to a trader's usual size.",                         0.80],
    ["Follow your favorites","Track specific traders, leaderboard or not.",                            0.60],
  ];
  return (
    <Screen>
      <Brand pulse />
      <h1 style={{ fontSize:38, lineHeight:1.06, fontWeight:700, letterSpacing:-1, margin:"28px 0 14px" }}>Know when the leaders move.</h1>
      <p style={{ fontSize:16.5, lineHeight:1.45, color:C.textMuted, margin:"0 0 32px" }}>Get notified the moment a top Polymarket trader opens an unusually large position.</p>
      <div style={{ display:"flex", flexDirection:"column", gap:20, marginBottom:26 }}>
        {features.map(([t, b, fill]) => (
          <div key={t} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
            <div style={{ width:42, height:42, borderRadius:12, background:C.greenDim, border:`1px solid ${C.border}`, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:7, flexShrink:0 }}>
              <div style={{ width:6, height:`${fill*100}%`, borderRadius:3, background:C.green, boxShadow:`0 0 8px ${C.green}66` }} />
            </div>
            <div><div style={{ fontSize:16.5, fontWeight:600, marginBottom:2 }}>{t}</div><div style={{ fontSize:14.5, color:C.textMuted, lineHeight:1.4 }}>{b}</div></div>
          </div>
        ))}
      </div>
      <div style={{ border:`1px solid ${C.amber}44`, background:C.amberDim, borderRadius:16, padding:20, marginBottom:20 }}>
        <div style={{ color:C.amber, fontWeight:600, fontSize:15.5, marginBottom:10 }}>Not financial advice</div>
        <p style={{ fontSize:14, lineHeight:1.55, color:C.text, margin:"0 0 16px" }}>This app shows public trading activity for informational purposes only. It does not recommend trades. Prediction markets carry real risk of loss, and past activity does not predict future results.</p>
        <label style={{ display:"flex", gap:12, alignItems:"flex-start", cursor:"pointer", fontSize:14.5, lineHeight:1.4 }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ width:22, height:22, marginTop:1, accentColor:C.green, flexShrink:0, cursor:"pointer" }} />
          <span>I understand this isn't financial advice and trading involves risk.</span>
        </label>
      </div>
      <Footer>Not affiliated with or endorsed by Polymarket.</Footer>
      <PrimaryButton disabled={!ack} onClick={onContinue}>Continue</PrimaryButton>
    </Screen>
  );
}

function Priming({ onEnable, onSkip, sampleAlert }) {
  const C = useTheme();
  return (
    <Screen>
      <Brand />
      <h1 style={{ fontSize:36, lineHeight:1.07, fontWeight:700, letterSpacing:-1, margin:"28px 0 14px" }}>Turn on alerts.</h1>
      <p style={{ fontSize:16.5, lineHeight:1.45, color:C.textMuted, margin:"0 0 24px" }}>We'll only ping you when a leader makes a real move — a position well above their usual size. Here's what one looks like:</p>
      <div style={{ marginBottom:24 }}>
        <AlertCard a={sampleAlert} sample read={false} onRead={() => {}} />
      </div>
      <div style={{ display:"flex", gap:12, alignItems:"flex-start", background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:"14px 16px" }}>
        <span style={{ width:7, height:7, borderRadius:"50%", background:C.green, marginTop:7, flexShrink:0, boxShadow:`0 0 8px ${C.green}88` }} />
        <p style={{ fontSize:14, lineHeight:1.5, color:C.textMuted, margin:0 }}>You can tune sensitivity and quiet hours anytime in settings.</p>
      </div>
      <div style={{ marginTop:28 }}>
        <PrimaryButton onClick={onEnable}>Enable notifications</PrimaryButton>
        <button onClick={onSkip} style={{ width:"100%", padding:14, marginTop:10, borderRadius:14, border:"none", background:"transparent", color:C.textMuted, fontSize:15, fontWeight:500, cursor:"pointer" }}>Maybe later</button>
      </div>
    </Screen>
  );
}

const WALKTHROUGH_STEPS = 5;

function WalkthroughLegendRow({ icon, title, body }) {
  const C = useTheme();
  return (
    <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:18 }}>
      <div style={{ width:34, height:34, borderRadius:10, background:C.greenDim, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:15, color:C.green }}>
        {icon ?? "•"}
      </div>
      <div><div style={{ fontSize:15, fontWeight:600, marginBottom:2 }}>{title}</div><div style={{ fontSize:13.5, color:C.textMuted, lineHeight:1.4 }}>{body}</div></div>
    </div>
  );
}

function Walkthrough({ onDone, sampleAlert }) {
  const C = useTheme();
  const [step, setStep] = useState(0);
  const last = step === WALKTHROUGH_STEPS - 1;
  const next = () => (last ? onDone() : setStep((s) => s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <Screen>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div style={{ display:"flex", gap:6 }}>
          {Array.from({ length: WALKTHROUGH_STEPS }).map((_, i) => (
            <div key={i} style={{ width:7, height:7, borderRadius:4, background: i === step ? C.green : C.border }} />
          ))}
        </div>
        <span onClick={onDone} style={{ fontSize:14, fontWeight:500, color:C.textMuted, cursor:"pointer" }}>Skip</span>
      </div>

      {step === 0 && (
        <>
          <div style={{ width:56, height:56, borderRadius:16, background:C.greenDim, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, color:C.green, marginBottom:22 }}>⚡</div>
          <h1 style={{ fontSize:30, lineHeight:1.08, fontWeight:700, letterSpacing:-0.8, margin:"0 0 14px" }}>Know when the leaders move.</h1>
          <p style={{ fontSize:15.5, lineHeight:1.5, color:C.textMuted, margin:0 }}>
            Follow the Leader watches the top traders on Polymarket and alerts you the moment
            one of them opens an unusually large or high-conviction position. It's read-only —
            we never place trades for you.
          </p>
        </>
      )}

      {step === 1 && (
        <>
          <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:"0 0 10px" }}>Reading an alert</h1>
          <p style={{ fontSize:15, color:C.textMuted, margin:"0 0 20px" }}>Every card packs in a lot — here's what each piece means.</p>
          <div style={{ marginBottom:22 }}>
            <AlertCard a={sampleAlert} sample read={false} onRead={() => {}} />
          </div>
          <WalkthroughLegendRow title="#1" body="Their rank on today's leaderboard." />
          <WalkthroughLegendRow title="71% win" body={`Win rate on their recently resolved markets — flagged "small sample" when there isn't much history yet.`} />
          <WalkthroughLegendRow title="3.2× usual" body="This trade is that many times bigger than their typical position size." />
          <WalkthroughLegendRow title="Bought YES" body="The side and outcome of the position." />
          <WalkthroughLegendRow title="$48,200 @ 0.61" body="Trade size and entry price." />
          <WalkthroughLegendRow title="→ $79,016 to win" body="What they'd collect if this resolves in their favor." />
          <WalkthroughLegendRow title="market ↗" body="Opens the live market on Polymarket." />
        </>
      )}

      {step === 2 && (
        <>
          <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:"0 0 10px" }}>Whale & Dormant Wallet tabs</h1>
          <p style={{ fontSize:15, color:C.textMuted, margin:"0 0 20px" }}>Two dedicated feeds, always on — independent of your regular filters.</p>
          <WalkthroughLegendRow icon="◆" title="Whale" body="Any trade over your whale threshold, regardless of conviction." />
          <WalkthroughLegendRow icon="☾" title="Dormant Wallet" body="A trader who'd gone quiet suddenly trades again." />
          <p style={{ fontSize:13.5, color:C.textMuted, lineHeight:1.5, marginTop:6 }}>
            These bypass your conviction and win-rate settings entirely, so you never miss one.
          </p>
        </>
      )}

      {step === 3 && (
        <>
          <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:"0 0 10px" }}>Tuning your alerts</h1>
          <p style={{ fontSize:15, color:C.textMuted, margin:"0 0 20px" }}>Settings controls exactly what reaches your main Alerts feed:</p>
          <WalkthroughLegendRow title="Conviction multiplier" body="How many times bigger than usual a trade must be." />
          <WalkthroughLegendRow title="Minimum win rate" body="Filter out traders below a win-rate floor." />
          <WalkthroughLegendRow title="Whale $ threshold" body="Alert on any trade over a dollar amount." />
          <WalkthroughLegendRow title="Dormancy days" body="How long a trader must go quiet before reactivating counts." />
          <p style={{ fontSize:13.5, color:C.textMuted, lineHeight:1.5, marginTop:6 }}>
            You can change any of these anytime — nothing here is permanent.
          </p>
        </>
      )}

      {step === 4 && (
        <>
          <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:"0 0 18px" }}>Favorites & notifications</h1>
          <WalkthroughLegendRow icon="★" title="Favorites" body="Star a trader to always see their moves, bypassing every filter above." />
          <WalkthroughLegendRow icon="⚙" title="Push notifications" body="Get pinged the moment an alert fires." />
          <WalkthroughLegendRow icon="☾" title="Quiet hours" body="Mute notifications overnight — alerts still log to the feed." />
        </>
      )}

      <div style={{ display:"flex", alignItems:"center", gap:20, marginTop:28 }}>
        {step > 0 && <span onClick={back} style={{ fontSize:15, fontWeight:500, color:C.textMuted, cursor:"pointer" }}>Back</span>}
        <div style={{ flex:1 }}><PrimaryButton onClick={next}>{last ? "Start exploring" : "Next"}</PrimaryButton></div>
      </div>
    </Screen>
  );
}

// ════════════════════════════════════════════════════════════════════
// ALERT FEED
// ════════════════════════════════════════════════════════════════════
function AlertCard({ a, read, onRead, sample, favSet, rankWindow = "day" }) {
  const C = useTheme();
  const fav = favSet ? favSet.has(a.trader) : false;
  const url = marketUrl(a.slug, a.eventSlug);
  const windowRank   = { day: a.rank, week: a.weekRank, month: a.monthRank, all: a.allRank }[rankWindow];
  const periodLabel  = { day: "today", week: "this week", month: "this month", all: "all time" }[rankWindow];
  return (
    <div onClick={() => !read && !sample && onRead(a.txHash)}
         style={{ position:"relative", background:C.surface, border:`1px solid ${read||sample ? C.border : C.green+"33"}`, borderRadius:16, padding:16, cursor:read||sample?"default":"pointer", opacity:read?0.72:1, transition:"opacity 160ms, border-color 160ms" }}>
      {sample && <span style={{ position:"absolute", top:-9, left:16, fontSize:10.5, fontWeight:600, letterSpacing:0.4, textTransform:"uppercase", color:C.textMuted, background:C.bg, padding:"1px 8px", borderRadius:6, border:`1px solid ${C.border}` }}>Sample</span>}
      {!read && !sample && <span style={{ position:"absolute", top:16, right:16, width:8, height:8, borderRadius:"50%", background:C.green, boxShadow:`0 0 8px ${C.green}aa` }} />}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, paddingRight:16 }}>
        <Avatar />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
            {displayName(a.username, a.trader)}{fav && <span style={{ color:C.amber, fontSize:12 }}>★</span>}
          </div>
          <div style={{ fontSize:12, color:C.textMuted }}>
            {windowRank != null && <>#{windowRank} {periodLabel} · </>}
            <WinRate winRate={a.winRate} resolved={a.resolved} />
          </div>
        </div>
        {a.conviction != null
          ? <Badge title="How many times this trader's typical position size." bg={C.amberDim} bd={`${C.amber}55`} fg={C.amber} bold>{a.conviction.toFixed(1)}× usual</Badge>
          : fav ? <Badge title="From a trader you favorited — shown regardless of filters." bg="transparent" bd={`${C.amber}55`} fg={C.amber}>★ favorite</Badge>
          : null}
      </div>
      <div style={{ fontSize:15.5, fontWeight:600, lineHeight:1.35, marginBottom:10, display:"flex", alignItems:"center", flexWrap:"wrap", gap:6 }}>
        <SideBadge side={a.side} />
        <OutcomePill o={a.outcome} />
        <span>· {a.market}</span>
      </div>
      {(a.categories?.includes("whale") || a.dormantDays != null) && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
          {a.categories?.includes("whale") && (
            <Badge bg={C.amberDim} bd={`${C.amber}55`} fg={C.amber}>◆ whale</Badge>
          )}
          {a.categories?.includes("dormant_wallet") && a.dormantDays != null && (
            <Badge bg="transparent" bd={`${C.textMuted}55`} fg={C.textMuted}>
              ☾ inactive {Math.round(a.dormantDays)}d
            </Badge>
          )}
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", gap:12, fontSize:12.5, color:C.textMuted }}>
        <span style={{ fontWeight:600, color:C.text }}>{fmtUsd(a.usd, false)}</span>
        {a.price != null && <span>@ {a.price.toFixed(2)}</span>}
        {a.side === "BUY" && a.potentialPayout != null && (
          <span style={{ color:C.amber }}>
            → {fmtUsd(a.potentialPayout, false)} to win
            {a.impliedReturnPct != null && ` · +${a.impliedReturnPct.toFixed(0)}%`}
          </span>
        )}
        <span>· {timeAgo(a.ts)}</span>
        <span style={{ flex:1 }} />
        {url && <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color:C.textMuted, textDecoration:"none" }}>market ↗</a>}
      </div>
    </div>
  );
}

function AlertsTab({ favSet, rankCtl, settings, readSet, markRead, markAllRead, onUnreadChange }) {
  const { topN, favBypassRank, rankWindow, setRankWindow } = rankCtl;
  const { conviction, winFloor, favBypass, whaleUsd, dormancyDays, excludeWhale, excludeDormant } = settings;
  const excludedCategories = [excludeWhale && "whale", excludeDormant && "dormant_wallet"].filter(Boolean).join(",");

  const [alerts, setAlerts]   = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("all");
  const seenHashes = useRef(new Set());

  const changeRankWindow = (w) => {
    setRankWindow(w);
    saveSettings(DEVICE_ID, { rank_window: w });
  };

  // Load initial alert batch from REST
  useEffect(() => {
    setLoading(true);
    fetchAlerts({
      deviceId:      DEVICE_ID,
      convictionMin: conviction,
      winRateMin:    winFloor,
      topN,
      rankWindow,
      limit:         50,
      whaleUsdThreshold:     whaleUsd,
      dormancyThresholdDays: dormancyDays,
      excludedCategories,
    }).then(({ data, fromMock: fm }) => {
      setAlerts(data);
      setFromMock(fm);
      data.forEach((a) => seenHashes.current.add(a.txHash));
      setLoading(false);
    });
  }, [conviction, winFloor, topN, rankWindow, whaleUsd, dormancyDays, excludedCategories]);

  // WebSocket — prepend new alerts, dedupe by txHash
  useAlertStream(`${WS_BASE}/ws?device_id=${DEVICE_ID}`, (newAlert) => {
    if (seenHashes.current.has(newAlert.txHash)) return;
    seenHashes.current.add(newAlert.txHash);
    setAlerts((prev) => [newAlert, ...prev]);
  });

  // Client-side filter pass:
  // conviction & winFloor are already applied server-side, but if the user
  // changed sliders since the last fetch, re-filter locally until the next
  // fetch resolves. Rank gating and fav-bypass happen here too.
  const visible = useMemo(
    () => filterVisibleAlerts(alerts, {
      favSet, topN, favBypassRank, conviction, winFloor, favBypass, rankWindow,
      whaleUsd, dormancyDays, excludeWhale, excludeDormant,
    }),
    [alerts, favSet, topN, favBypassRank, conviction, winFloor, favBypass, rankWindow,
     whaleUsd, dormancyDays, excludeWhale, excludeDormant]
  );

  const displayed  = tab === "favorites" ? visible.filter((a) => favSet.has(a.trader)) : visible;
  const unread     = visible.filter((a) => !readSet.has(a.txHash)).length;
  const markAll    = () => markAllRead(visible.map((a) => a.txHash));

  // Report unread count up to root so the TabBar badge reflects reality
  useEffect(() => { onUnreadChange(unread); }, [unread, onUnreadChange]);

  return (
    <Page>
      <Header title="Alerts" badge={unread || null} action={unread ? { label:"Mark all read", onClick:markAll } : null} />
      {fromMock && <MockBanner />}
      <Pills value={rankWindow} onChange={changeRankWindow} options={[["day","Day"],["week","Week"],["month","Month"],["all","All"]]} dimLast />
      {rankWindow === "all" && <Note tone="amber">All-time ranking can surface stale signals — a single lucky trade can dominate. Day view is better for live activity.</Note>}
      <Pills value={tab} onChange={setTab} options={[["all","All"],["favorites","Favorites"]]} />
      <RankChips rankCtl={rankCtl} />
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:18 }}>
        {loading
          ? <Spinner />
          : displayed.length === 0
            ? <Empty>{tab==="favorites" ? "No alerts from your favorites yet." : `No alerts matching your current filters. Try lowering conviction or widening the rank filter.`}</Empty>
            : displayed.map((a) => <AlertCard key={a.txHash} a={a} read={readSet.has(a.txHash)} onRead={markRead} favSet={favSet} rankWindow={rankWindow} />)}
      </div>
      <Footer>Public trading activity from Polymarket, shown for information only. Not affiliated with or endorsed by Polymarket. Not financial advice.</Footer>
    </Page>
  );
}

// A dedicated, unfiltered feed of alerts tagged with one category (computed
// server-side from fixed system thresholds — see core.py's
// compute_alert_categories) — deliberately separate from AlertsTab's
// conviction/win-rate/rank-gated feed, and independent of a device's own
// whale/dormancy settings or excluded-categories toggle: always shows
// every alert with the tag, regardless of either.
function CategoryAlertsTab({ category, title, emptyText, favSet, readSet, markRead, markAllRead }) {
  const [alerts, setAlerts]     = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading]   = useState(true);
  const seenHashes = useRef(new Set());

  useEffect(() => {
    setLoading(true);
    fetchAlerts({ deviceId: DEVICE_ID, topN: RANK_MAX, limit: 50, category })
      .then(({ data, fromMock: fm }) => {
        setAlerts(data);
        setFromMock(fm);
        data.forEach((a) => seenHashes.current.add(a.txHash));
        setLoading(false);
      });
  }, [category]);

  // main.py's broadcast() sends every category-tagged trade to every
  // connected device regardless of that device's own regular filters,
  // specifically so this live stream works here.
  useAlertStream(`${WS_BASE}/ws?device_id=${DEVICE_ID}`, (newAlert) => {
    if (!newAlert.categories?.includes(category)) return;
    if (seenHashes.current.has(newAlert.txHash)) return;
    seenHashes.current.add(newAlert.txHash);
    setAlerts((prev) => [newAlert, ...prev]);
  });

  const visible = useMemo(() => {
    const seen = new Set();
    return alerts
      .filter((a) => {
        if (seen.has(a.txHash)) return false;
        seen.add(a.txHash);
        return a.categories?.includes(category);
      })
      .sort((x, y) => y.ts - x.ts);
  }, [alerts, category]);

  const unread  = visible.filter((a) => !readSet.has(a.txHash)).length;
  const markAll = () => markAllRead(visible.map((a) => a.txHash));

  return (
    <Page>
      <Header title={title} badge={unread || null} action={unread ? { label:"Mark all read", onClick:markAll } : null} />
      {fromMock && <MockBanner />}
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:18 }}>
        {loading
          ? <Spinner />
          : visible.length === 0
            ? <Empty>{emptyText}</Empty>
            : visible.map((a) => <AlertCard key={a.txHash} a={a} read={readSet.has(a.txHash)} onRead={markRead} favSet={favSet} />)}
      </div>
      <Footer>Public trading activity from Polymarket, shown for information only. Not affiliated with or endorsed by Polymarket. Not financial advice.</Footer>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════
// LEADERBOARD
// ════════════════════════════════════════════════════════════════════
function TraderRow({ t, fav, onFav, onOpen }) {
  const C = useTheme();
  return (
    <div onClick={() => onOpen(t)} role="button" tabIndex={0}
         style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, cursor:"pointer" }}>
      <div style={{ width:28, textAlign:"center", fontSize:14, fontWeight:700, color:t.rank<=3?C.green:C.textMuted, flexShrink:0 }}>{t.rank}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:3 }}>{displayName(t.username, t.id)}</div>
        <div style={{ fontSize:12.5 }}><WinRate winRate={t.winRate} resolved={t.resolved} /></div>
      </div>
      {/* PnL for whichever window this leaderboard is currently sorted by */}
      <div style={{ fontSize:15, fontWeight:700, color:t.pnlPeriod>=0?C.green:C.red, flexShrink:0 }}>{fmtUsd(t.pnlPeriod)}</div>
      <button onClick={(e) => { e.stopPropagation(); onFav(t.id); }}
              title={fav?"Favorited — bypasses filters.":"Favorite to bypass filters."}
              style={{ background:"transparent", border:"none", cursor:"pointer", padding:6, color:fav?C.amber:C.textMuted, fontSize:18 }}>
        {fav ? "★" : "☆"}
      </button>
    </div>
  );
}

function LeaderboardTab({ favSet, toggleFav, onOpen, rankCtl }) {
  const C = useTheme();
  const { topN, favBypassRank } = rankCtl;
  const [win, setWin]       = useState("day");
  const [traders, setTraders] = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLeaderboard(win).then(({ data, fromMock: fm }) => {
      setTraders(data);
      setFromMock(fm);
      setLoading(false);
    });
  }, [win]);

  const favs = traders.filter((t) => favSet.has(t.id));
  const rest = traders.filter((t) => !favSet.has(t.id) && t.rank <= topN);
  const hiddenCount = traders.filter((t) => !favSet.has(t.id) && t.rank > topN).length;

  return (
    <Page>
      <Header title="Leaderboard" />
      {fromMock && <MockBanner />}
      <Pills value={win} onChange={setWin} options={[["day","Day"],["week","Week"],["month","Month"],["all","All"]]} dimLast />
      <RankChips rankCtl={rankCtl} />
      {win === "all" && <Note tone="amber">All-time ranking can surface stale signals — a single lucky trade can dominate. Day view is better for live activity.</Note>}
      {loading ? <Spinner /> : (
        <>
          {favs.length > 0 && (
            <div style={{ marginTop:20, marginBottom:22 }}>
              <SectionLabel color={C.amber}>★ Your favorites</SectionLabel>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {favs.map((t) => <TraderRow key={t.id} t={t} fav onFav={toggleFav} onOpen={onOpen} />)}
              </div>
            </div>
          )}
          <div style={{ marginTop:favs.length?0:20 }}>
            {favs.length > 0 && <SectionLabel>Top today{topN < RANK_MAX ? ` · top ${topN}` : ""}</SectionLabel>}
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {rest.map((t) => <TraderRow key={t.id} t={t} fav={false} onFav={toggleFav} onOpen={onOpen} />)}
            </div>
            {hiddenCount > 0 && <p style={{ fontSize:12, color:C.textMuted, textAlign:"center", marginTop:14, opacity:0.75 }}>{hiddenCount} more outside the top {topN}. Widen the rank filter to see them.</p>}
          </div>
        </>
      )}
      <Footer>Data from Polymarket's public leaderboard. Not affiliated with or endorsed by Polymarket.</Footer>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════
// FAVORITES TAB
// ════════════════════════════════════════════════════════════════════
function FavoritesTab({ favSet, toggleFav, onOpen, traders }) {
  const favs = traders.filter((t) => favSet.has(t.id));
  return (
    <Page>
      <Header title="Favorites" />
      {favs.length === 0
        ? <Empty>No favorites yet. Star traders on the leaderboard to follow every move they make — even ones that don't clear your filters.</Empty>
        : (
          <>
            <Note tone="amber">Alerts from these traders bypass your conviction and win-rate filters.</Note>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:18 }}>
              {favs.map((t) => <TraderRow key={t.id} t={t} fav onFav={toggleFav} onOpen={onOpen} />)}
            </div>
          </>
        )}
      <Footer>Not affiliated with or endorsed by Polymarket.</Footer>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════
// TRADER DETAIL
// ════════════════════════════════════════════════════════════════════
function TraderDetail({ trader, favSet, toggleFav, onBack, settings }) {
  const C = useTheme();
  const t = trader;
  const [open,   setOpen]   = useState([]);
  const [closed, setClosed] = useState([]);
  // Real win_rate/resolved from closed-positions — NOT t.winRate/t.resolved,
  // which come from the leaderboard row and are always null/0.
  const [winRate,  setWinRate]  = useState(null);
  const [resolved, setResolved] = useState(0);
  const [value,  setValue]  = useState(null);
  const [valueHistory, setValueHistory] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesHasMore, setTradesHasMore] = useState(false);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exp, setExp]       = useState(null);
  const [timeline,        setTimeline]        = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const timelineRequestId = useRef(0);
  const fav = favSet.has(t.id);
  const pct = winRate != null ? Math.round(winRate * 100) : null;
  const small = resolved < 10;
  const TRADES_PAGE = 20;

  // Lazy-loads the buy/sell fill timeline the first time a closed position
  // is expanded, so a big realizedPnl with an empty "Open positions" list
  // is explainable instead of looking like a data error.
  //
  // rowKey identifies the card (unique even when /closed-positions returns
  // multiple rows for the same market — e.g. separate YES/NO outcome entries
  // — which share a conditionId); conditionId is the real market id used
  // for the timeline fetch, which is not outcome-scoped.
  const toggleExpanded = async (rowKey, conditionId) => {
    if (exp === rowKey) { setExp(null); return; }
    setExp(rowKey);
    setTimeline([]);
    setTimelineLoading(true);
    const requestId = ++timelineRequestId.current;
    try {
      const tl = await fetchPositionTimeline(t.id, conditionId);
      // A newer expand/collapse may have fired while this was in flight —
      // discard a late response so it can't overwrite a different
      // position's timeline.
      if (requestId !== timelineRequestId.current) return;
      setTimeline(tl.data);
    } finally {
      if (requestId === timelineRequestId.current) setTimelineLoading(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard?.writeText(t.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchOpenPositions(t.id),
      fetchClosedPositions(t.id),
      fetchWalletValue(t.id),
      fetchValueHistory(t.id),
    ]).then(([o, c, v, vh]) => {
      setOpen(o.data);
      setClosed(c.data);
      setWinRate(c.winRate);
      setResolved(c.resolved);
      setValue(v.data);
      setValueHistory(vh.data);
      setFromMock(o.fromMock || c.fromMock);
      setLoading(false);
    });
  }, [t.id]);

  // Separate effect (not folded into the one above): trade history depends
  // on settings.conviction too, since the list is filtered server-side to
  // the user's conviction threshold — a slider change while this panel is
  // open should re-fetch the trade list without re-fetching everything else.
  useEffect(() => {
    fetchTradeHistory(t.id, { limit: TRADES_PAGE, offset: 0, convictionMin: settings.conviction }).then((tr) => {
      setTrades(tr.data);
      // Offset advances by the raw page size, not tr.data.length — the
      // conviction filter runs server-side on a fixed-size raw window, so a
      // filtered page is often shorter than TRADES_PAGE. Advancing by the
      // filtered count would re-request (and re-filter) trades we've seen.
      setTradesOffset(TRADES_PAGE);
      setTradesHasMore(tr.hasMore);
    });
  }, [t.id, settings.conviction]);

  const loadMoreTrades = () => {
    setTradesLoading(true);
    fetchTradeHistory(t.id, { limit: TRADES_PAGE, offset: tradesOffset, convictionMin: settings.conviction }).then((tr) => {
      setTrades((prev) => [...prev, ...tr.data]);
      setTradesOffset((prev) => prev + TRADES_PAGE);
      setTradesHasMore(tr.hasMore);
      setTradesLoading(false);
    });
  };

  return (
    <Page>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:22 }}>
        <button onClick={onBack} style={{ background:"transparent", border:"none", color:C.textMuted, fontSize:22, cursor:"pointer", padding:4 }}>‹</button>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:18, fontWeight:700 }}>{displayName(t.username, t.id)}</span>
            {fav && <span style={{ color:C.amber, fontSize:16 }}>★</span>}
          </div>
          <div style={{ fontSize:13, color:C.textMuted }}>#{t.rank} today</div>
        </div>
        <button onClick={() => toggleFav(t.id)}
                style={{ background:"transparent", border:`1px solid ${C.border}`, color:fav?C.amber:C.textMuted, fontSize:13, fontWeight:600, padding:"6px 12px", borderRadius:9, cursor:"pointer" }}>
          {fav ? "★ Favorited" : "☆ Favorite"}
        </button>
      </div>

      {/* Full wallet address — visible + copyable, so users can paste it
          into polymarket.com themselves rather than being stuck with the
          shortened display everywhere else. */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
        <span style={{ fontSize:12, color:C.textMuted, fontFamily:"monospace", userSelect:"all", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {t.id}
        </span>
        <button onClick={copyAddress}
                style={{ background:"transparent", border:"none", color:C.green, fontSize:12, fontWeight:600, cursor:"pointer", flexShrink:0, padding:0 }}>
          {copied ? "Copied ✓" : "Copy ⧉"}
        </button>
      </div>

      {fromMock && <MockBanner />}

      {valueHistory.length >= 2 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"14px 16px", marginBottom:10 }}>
          <ValueChart points={valueHistory} stroke={C.green} fill={`${C.green}22`} />
        </div>
      )}

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"18px 16px", marginBottom:10, display:"flex", gap:12, flexWrap:"wrap" }}>
        {/* "Open trades" — value of currently-open positions only, a
            point-in-time snapshot. Cumulative PnL for a period can (and
            often does) far exceed this, since it includes gains/losses
            already realized and no longer "open" — see Volume. */}
        <Stat label="Open trades" value={value != null ? fmtUsd(value, false) : "—"} />
        <Stat label="Win rate"  value={pct != null ? `${pct}%` : "—"} color={pct >= 60 ? C.green : C.text} />
        <Stat label="PnL (day)" value={fmtUsd(t.pnlDay)} color={C.green} />
        <Stat label="PnL (all)" value={fmtUsd(t.pnlAll)} color={C.green} />
        {/* Volume — for whichever leaderboard window (day/week/month/all)
            got you to this trader, i.e. the same window as t.pnlPeriod. */}
        <Stat label="Volume" value={fmtUsd(t.volPeriod, false)} />
        <Stat label="Resolved"  value={resolved} />
      </div>

      {small && <Note tone="amber">Small sample — only {resolved} resolved markets. Win rate may not be representative yet.</Note>}

      {loading ? <Spinner /> : (
        <>
          <div style={{ height:20 }} />
          <SectionLabel>Open positions</SectionLabel>
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:26 }}>
            {open.length === 0
              ? <Empty>No open positions found.</Empty>
              // Same conditionId can appear twice (separate YES/NO outcome
              // rows for one market) — see closed positions below.
              : open.map((p, idx) => (
                <div key={`${p.conditionId ?? p.id ?? p.market}::${p.outcome}::${idx}`} style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14.5, fontWeight:600, marginBottom:6, lineHeight:1.3 }}>
                      <MarketLink slug={p.slug} eventSlug={p.eventSlug} color={C.text}>{p.market}</MarketLink>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:12.5, color:C.textMuted }}>
                      <OutcomePill o={p.outcome} />@ {p.price?.toFixed(2) ?? "—"}
                    </div>
                  </div>
                  {/* currentValue = worth now; positionSizeUsd = real USD
                      entry cost. Both matter and can differ a lot — showing
                      only one (previously the raw, mislabeled 'size' share
                      count) was misleading. */}
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:15, fontWeight:700 }}>{fmtUsd(p.currentValue, false)}</div>
                    <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>Entry {fmtUsd(p.positionSizeUsd, false)}</div>
                  </div>
                </div>
              ))}
          </div>

          <SectionLabel>Resolved history <span style={{ color:C.textMuted, fontWeight:400, textTransform:"none", letterSpacing:0, marginLeft:6, fontSize:12.5 }}>tap for detail</span></SectionLabel>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {closed.length === 0
              ? <Empty>No resolved positions found.</Empty>
              : closed.map((p, idx) => {
                  // /closed-positions can return multiple rows sharing one
                  // conditionId (e.g. separate YES/NO outcome entries for
                  // the same market) — conditionId alone isn't a unique key.
                  const pKey = `${p.conditionId ?? p.id ?? p.market}::${p.outcome}::${idx}`;
                  return (
                <div key={pKey} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
                  <div onClick={() => toggleExpanded(pKey, p.conditionId)} role="button" tabIndex={0}
                       style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", cursor:"pointer" }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:p.won?C.green:C.red, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0, fontSize:14, fontWeight:600, lineHeight:1.3, whiteSpace:exp===pKey?"normal":"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.market}</div>
                    <div style={{ fontSize:14.5, fontWeight:700, color:p.won?C.green:C.red, flexShrink:0 }}>{fmtUsd(p.pnl)}</div>
                    <span style={{ color:C.textMuted, fontSize:12, transform:exp===pKey?"rotate(180deg)":"none", transition:"transform 120ms" }}>▾</span>
                  </div>
                  {exp === pKey && (
                    <div style={{ padding:"0 14px 14px 34px", display:"flex", flexDirection:"column", gap:10, fontSize:12.5, color:C.textMuted }}>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:14 }}>
                        <span style={{ display:"inline-flex", gap:6, alignItems:"center" }}>Position <OutcomePill o={p.outcome} /></span>
                        {/* Real USD cost basis — backend-computed from
                            totalBought(shares) × avgPrice, not either raw
                            field alone (totalBought is a share count despite
                            the name — see core.py). */}
                        <span>Size {fmtUsd(p.positionSizeUsd, false)}</span>
                        <span>Result <span style={{ color:p.won?C.green:C.red, fontWeight:600 }}>{p.won?"Won":"Lost"}</span></span>
                        <span>Resolved {p.at ?? "—"}</span>
                        <MarketLink slug={p.slug} eventSlug={p.eventSlug}>View market</MarketLink>
                      </div>
                      {/* Buy/sell timeline — explains a big realizedPnl with
                          no matching "open position": when it was entered,
                          whether it was sold early vs. held to resolution. */}
                      <div>
                        <div style={{ fontWeight:600, marginBottom:2 }}>Timeline</div>
                        {timelineLoading ? (
                          <div>Loading…</div>
                        ) : timeline.length === 0 ? (
                          <div>No fill history found.</div>
                        ) : (
                          <>
                            <div>Entered {timeAgo(timeline[0].ts)} ({fmtUsd(timeline[0].usd, false)} first fill)</div>
                            {timeline.some((tl) => tl.side === "SELL") ? (
                              <div>Sold before resolution — an early exit, not a hold-to-resolution win.</div>
                            ) : (
                              <div>Held to resolution {p.resolvedTs ? timeAgo(p.resolvedTs) : "—"}.</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                  );
              })}
          </div>

          <div style={{ height:26 }} />
          <SectionLabel>Trade history</SectionLabel>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {trades.length === 0
              ? <Empty>No trade history found.</Empty>
              : trades.map((tr) => (
                <div key={tr.txHash} style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 14px", background:C.surface, border:`1px solid ${C.border}`, borderRadius:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600, marginBottom:6, lineHeight:1.3, display:"flex", alignItems:"center", gap:6 }}>
                      <SideBadge side={tr.side} />
                      <MarketLink slug={tr.slug} eventSlug={tr.eventSlug} color={C.text}>{tr.market}</MarketLink>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:12.5, color:C.textMuted }}>
                      <OutcomePill o={tr.outcome} />@ {tr.price?.toFixed(2) ?? "—"} · {timeAgo(tr.ts)}
                    </div>
                  </div>
                  <div style={{ fontSize:15, fontWeight:700 }}>{fmtUsd(tr.usd, false)}</div>
                </div>
              ))}
            {tradesHasMore && (
              <button onClick={loadMoreTrades} disabled={tradesLoading}
                      style={{ padding:12, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:13.5, fontWeight:600, cursor:tradesLoading?"default":"pointer" }}>
                {tradesLoading ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </>
      )}
      <Footer>Positions and history from Polymarket's public data, shown for information only. Not affiliated with or endorsed by Polymarket. Not financial advice.</Footer>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════
// SETTINGS
// ════════════════════════════════════════════════════════════════════
const SETTINGS_DEFAULTS = {
  conviction:2.0, winFloor:0.6, favBypass:true, notif:true, quiet:false, from:"22:00", to:"08:00",
  whaleUsd: 0, dormancyDays: 5.0, excludeWhale: false, excludeDormant: false,
};

function SettingsTab({ favSet, toggleFav, rankCtl, settings, setSettings, traders, onReplayWalkthrough }) {
  const C = useTheme();
  const { mode, toggle } = useThemeMode();
  const usernameById = useMemo(() => new Map(traders.map((t) => [t.id, t.username])), [traders]);
  const { topN, setTopN, favBypassRank, setFavBypassRank, setRankWindow } = rankCtl;
  const [savedAt, setSavedAt]     = useState(null);
  const [backendSaved, setBackendSaved] = useState(false);
  const saveTimer   = useRef(null);
  const debounceRef = useRef(null);

  const persist = useCallback((patch) => {
    // Debounce backend saves by 800 ms so sliders don't flood the API
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const nextExcludeWhale   = patch.excludeWhale   ?? settings.excludeWhale;
      const nextExcludeDormant = patch.excludeDormant ?? settings.excludeDormant;
      const { error } = await saveSettings(DEVICE_ID, {
        conviction_multiplier: patch.conviction ?? settings.conviction,
        win_rate_floor:        patch.winFloor   ?? settings.winFloor,
        top_n:                 patch.top_n            ?? topN,
        fav_bypass_rank:       patch.fav_bypass_rank  ?? favBypassRank,
        fav_bypass_filters:    patch.favBypass  ?? settings.favBypass,
        notif_enabled:         patch.notif      ?? settings.notif,
        quiet_from:            (patch.quiet ?? settings.quiet) ? (patch.from ?? settings.from) : null,
        quiet_to:              (patch.quiet ?? settings.quiet) ? (patch.to   ?? settings.to)   : null,
        whale_usd_threshold:     patch.whaleUsd     ?? settings.whaleUsd,
        dormancy_threshold_days: patch.dormancyDays ?? settings.dormancyDays,
        excluded_categories:     [nextExcludeWhale && "whale", nextExcludeDormant && "dormant_wallet"].filter(Boolean).join(","),
      });
      setBackendSaved(!error);
    }, 800);

    // Show "Saved" indicator immediately (optimistic)
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSavedAt(Date.now()), 200);
  }, [settings, topN, favBypassRank]);

  const up = (patch) => { setSettings((p) => ({ ...p, ...patch })); persist(patch); };
  const setRank = (n) => { setTopN(n); persist({ top_n: n }); };

  const favs = [...favSet].map((id) => ({ id, winRate: null, resolved: 0 })); // populated from leaderboard in parent
  const saved = savedAt && Date.now() - savedAt < 2500;

  return (
    <Page>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:24 }}>
        <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:0 }}>Settings</h1>
        <span style={{ fontSize:12.5, color:C.green, opacity:saved?1:0, transition:"opacity 200ms" }}>
          {backendSaved ? "✓ Saved" : "✓ Saved locally"}
        </span>
      </div>

      <SectionLabel>Help</SectionLabel>
      <Card>
        <div onClick={onReplayWalkthrough} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
          <div>
            <div style={{ fontSize:15, fontWeight:500 }}>How alerts work</div>
            <div style={{ fontSize:12.5, color:C.textMuted, marginTop:3 }}>Replay the walkthrough</div>
          </div>
          <span style={{ fontSize:16, color:C.textMuted }}>›</span>
        </div>
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Appearance</SectionLabel>
      <Card>
        <Switch label="Dark mode" sub="Switch between sage light and dark." on={mode === "dark"} onChange={toggle} />
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Leaderboard rank</SectionLabel>
      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
          <span style={{ fontSize:15, fontWeight:500 }}>Show top traders</span>
          <span style={{ fontSize:15, fontWeight:700, color:C.green }}>{topN >= RANK_MAX ? "All (200)" : `Top ${topN}`}</span>
        </div>
        <div style={{ fontSize:12.5, color:C.textMuted, marginBottom:12, lineHeight:1.4 }}>Only see trades from traders ranked within this many on today's leaderboard.</div>
        <input type="range" min={1} max={RANK_MAX} step={1} value={topN} onChange={(e) => setRank(parseInt(e.target.value,10))} style={{ width:"100%", accentColor:C.green }} />
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMuted, marginTop:2 }}><span>Top 1 (tightest)</span><span>All 200</span></div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:12 }}>
          {RANK_CHIPS.map((n) => {
            const on = topN === n;
            return <button key={n} onClick={() => setRank(n)} style={{ padding:"5px 12px", borderRadius:999, fontSize:12.5, fontWeight:600, cursor:"pointer", border:`1px solid ${on?C.green+"66":C.border}`, background:on?C.greenDim:"transparent", color:on?C.green:C.textMuted }}>{n===RANK_MAX?"All":n}</button>;
          })}
        </div>
        <Divider />
        <Switch label="Favorites bypass rank filter" sub="Favorited traders alert you even when they fall outside the top N today." on={favBypassRank} onChange={(v) => { setFavBypassRank(v); persist({ fav_bypass_rank: v }); }} />
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Alert sensitivity</SectionLabel>
      <Card>
        <Slider label="Conviction multiplier" value={`${settings.conviction.toFixed(1)}×`} color={C.amber}
                desc="Alert only when a trade is at least this many times the trader's own usual position size."
                min={1} max={5} step={0.1} v={settings.conviction} onChange={(v) => up({ conviction:v })} lo="1.0× (noisy)" hi="5.0× (huge only)" />
        <Divider />
        <Slider label="Minimum win rate" value={`${Math.round(settings.winFloor*100)}%`} color={C.green}
                desc="Based on the trader's recently resolved markets. Traders without much history yet are flagged 'small sample,' not hidden."
                min={0} max={0.9} step={0.05} v={settings.winFloor} onChange={(v) => up({ winFloor:v })} lo="0% (all)" hi="90%" />
        <Divider />
        <Switch label="Whale alert (absolute $)" sub="Also alert on any trade over this dollar amount, regardless of conviction."
                on={settings.whaleUsd > 0} onChange={(v) => up({ whaleUsd: v ? 25000 : 0 })} />
        {settings.whaleUsd > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:12 }}>
            <span style={{ fontSize:15, fontWeight:600, color:C.text }}>$</span>
            <input type="number" min={0} step={1000} value={settings.whaleUsd}
                   onChange={(e) => up({ whaleUsd: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                   style={{ flex:1, fontSize:15, fontWeight:600, padding:"6px 10px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.text }} />
          </div>
        )}
        <Divider />
        <Slider label="Dormant-wallet alert" value={`${settings.dormancyDays.toFixed(0)}d`} color={C.textMuted}
                desc="Also alert when a trader who's been quiet this many days suddenly trades again."
                min={1} max={90} step={1} v={settings.dormancyDays} onChange={(v) => up({ dormancyDays: Math.round(v) })} lo="1 day" hi="90 days" />
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Alert categories</SectionLabel>
      <Card>
        <Switch label="Hide whale-tagged alerts" sub={`Alerts over $${WHALE_CATEGORY_USD_FLOOR.toLocaleString()} are tagged "whale" — hide them even if otherwise alert-worthy.`}
                on={settings.excludeWhale} onChange={(v) => up({ excludeWhale:v })} />
        <Divider />
        <Switch label="Hide dormant-wallet alerts" sub={`Trades from wallets inactive ${DORMANCY_CATEGORY_DAYS.toFixed(0)}+ days are tagged "dormant_wallet" — hide them.`}
                on={settings.excludeDormant} onChange={(v) => up({ excludeDormant:v })} />
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Favorites</SectionLabel>
      <Card>
        <Switch label="Favorites bypass filters" sub="Trust these traders enough to see every move." on={settings.favBypass} onChange={(v) => up({ favBypass:v })} />
        {favSet.size > 0 && (
          <>
            <Divider />
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[...favSet].map((id) => (
                <div key={id} style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ color:C.amber, fontSize:15 }}>★</span>
                  <div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:14, fontWeight:600 }}>{displayName(usernameById.get(id), id)}</div></div>
                  <button onClick={() => toggleFav(id)} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.textMuted, fontSize:12.5, padding:"5px 12px", borderRadius:8, cursor:"pointer" }}>Remove</button>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
      <div style={{ height:22 }} />

      <SectionLabel>Notifications</SectionLabel>
      <Card>
        <Switch label="Push notifications" sub="Get pinged when an alert fires." on={settings.notif} onChange={(v) => up({ notif:v })} />
        <Divider />
        <Switch label="Quiet hours" sub="Mute notifications during a window. Alerts still log to the feed." on={settings.quiet} onChange={(v) => up({ quiet:v })} />
        {settings.quiet && (
          <div style={{ display:"flex", gap:12, marginTop:16 }}>
            <TimeField label="From" value={settings.from} onChange={(v) => up({ from:v })} />
            <TimeField label="To"   value={settings.to}   onChange={(v) => up({ to:v  })} />
          </div>
        )}
      </Card>
      <div style={{ height:22 }} />

      <button onClick={() => { setSettings(SETTINGS_DEFAULTS); setTopN(RANK_MAX); setFavBypassRank(true); setRankWindow("day"); persist({ ...SETTINGS_DEFAULTS, top_n: RANK_MAX, fav_bypass_rank: true, rank_window: "day" }); }}
              style={{ width:"100%", padding:13, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:14, fontWeight:500, cursor:"pointer" }}>
        Reset to defaults
      </button>
      <Footer>Not affiliated with or endorsed by Polymarket. Not financial advice.</Footer>
    </Page>
  );
}

// ════════════════════════════════════════════════════════════════════
// LAYOUT PRIMITIVES
// ════════════════════════════════════════════════════════════════════
function Screen({ children }) {
  return <div style={{ width:"100%", maxWidth:440, margin:"0 auto", padding:"24px 16px 32px" }}>{children}</div>;
}
function Page({ children }) {
  return <div style={{ width:"100%", maxWidth:480, margin:"0 auto", padding:"20px 16px 28px" }}>{children}</div>;
}
function Brand({ pulse }) {
  const C = useTheme();
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ position:"relative", display:"flex" }}>
        <img src="/brand/ftl-mark-onDark.svg" alt="" width={40} height={40} />
        {pulse && <span style={{ position:"absolute", inset:-4, borderRadius:11, border:`1.5px solid ${C.green}`, animation:"ftlp 2.4s ease-out infinite" }} />}
      </div>
      <span style={{ fontSize:19, fontWeight:600, letterSpacing:-0.2 }}>Follow the Leader</span>
    </div>
  );
}
function Avatar() {
  const C = useTheme();
  return <div style={{ width:32, height:32, borderRadius:8, background:C.greenDim, border:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:C.green, flexShrink:0 }}>0x</div>;
}
function PrimaryButton({ children, disabled, onClick }) {
  const C = useTheme();
  return <button onClick={() => !disabled && onClick()} disabled={disabled} style={{ width:"100%", padding:16, borderRadius:14, border:"none", fontSize:16, fontWeight:600, cursor:disabled?"not-allowed":"pointer", background:disabled?C.greenDim:C.green, color:disabled?C.textMuted:C.bg, transition:"background 140ms, color 140ms", marginTop:4 }}>{children}</button>;
}
function ThemeToggleButton() {
  const C = useTheme();
  const { mode, toggle } = useThemeMode();
  const label = mode === "dark" ? "Switch to light mode" : "Switch to dark mode";
  return (
    <button
      onClick={toggle}
      aria-label={label}
      title={label}
      style={{
        background:   "transparent",
        border:       `1px solid ${C.border}`,
        borderRadius: 10,
        width:        34,
        height:       34,
        display:      "flex",
        alignItems:   "center",
        justifyContent: "center",
        cursor:       "pointer",
        color:        C.text,
        fontSize:     16,
        lineHeight:   1,
        padding:      0,
        flexShrink:   0,
      }}
    >
      {mode === "dark" ? "☀" : "☽"}
    </button>
  );
}
function Header({ title, badge, action }) {
  const C = useTheme();
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
      <h1 style={{ fontSize:26, fontWeight:700, letterSpacing:-0.6, margin:0, display:"flex", alignItems:"center", gap:10 }}>
        {title}
        {badge != null && <span style={{ fontSize:12, fontWeight:700, color:C.bg, background:C.green, borderRadius:999, padding:"2px 8px" }}>{badge}</span>}
      </h1>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        {action && <button onClick={action.onClick} style={{ background:"transparent", border:"none", color:C.textMuted, fontSize:13.5, fontWeight:500, cursor:"pointer" }}>{action.label}</button>}
        <ThemeToggleButton />
      </div>
    </div>
  );
}
function Pills({ value, onChange, options, dimLast }) {
  const C = useTheme();
  return (
    <div style={{ display:"flex", gap:6 }}>
      {options.map(([k, label], i) => {
        const active = value === k, dim = dimLast && i === options.length-1;
        return <button key={k} onClick={() => onChange(k)} style={{ flex:dimLast?1:"initial", padding:dimLast?"8px 0":"8px 16px", borderRadius:10, fontSize:13.5, fontWeight:600, cursor:"pointer", border:`1px solid ${active?C.green+"66":C.border}`, background:active?C.greenDim:"transparent", color:active?C.green:dim?C.textMuted+"99":C.textMuted }}>{label}</button>;
      })}
    </div>
  );
}
function Badge({ children, title, bg, bd, fg, bold }) {
  return <span title={title} style={{ background:bg, border:`1px solid ${bd}`, color:fg, fontSize:12.5, fontWeight:bold?700:600, padding:"3px 8px", borderRadius:8, whiteSpace:"nowrap" }}>{children}</span>;
}
function SectionLabel({ children, color }) {
  const C = useTheme();
  return <div style={{ fontSize:12, fontWeight:600, letterSpacing:0.5, textTransform:"uppercase", color:color ?? C.textMuted, marginBottom:10 }}>{children}</div>;
}
function Note({ children, tone = "amber" }) {
  const C = useTheme();
  const col = tone === "green" ? C.green : tone === "red" ? C.red : C.amber;
  const bg  = tone === "green" ? C.greenDim : tone === "red" ? C.redDim : C.amberDim;
  return <div style={{ fontSize:12.5, color:col, background:bg, border:`1px solid ${col}33`, borderRadius:10, padding:"9px 12px", margin:"14px 0 0", lineHeight:1.45 }}>{children}</div>;
}
function Empty({ children }) {
  const C = useTheme();
  return <div style={{ padding:"40px 18px", textAlign:"center", color:C.textMuted, fontSize:14.5, lineHeight:1.5, background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, marginTop:18 }}>{children}</div>;
}
function Card({ children }) {
  const C = useTheme();
  return <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:18 }}>{children}</div>;
}
function Divider() {
  const C = useTheme();
  return <div style={{ height:1, background:C.border, margin:"18px -18px" }} />;
}
function Stat({ label, value, color }) {
  const C = useTheme();
  return (
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:11.5, color:C.textMuted, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:700, color:color??C.text, whiteSpace:"nowrap" }}>{value}</div>
    </div>
  );
}
function Slider({ label, value, color, desc, min, max, step, v, onChange, lo, hi }) {
  const C = useTheme();
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
        <span style={{ fontSize:15, fontWeight:500 }}>{label}</span>
        <span style={{ fontSize:15, fontWeight:700, color }}>{value}</span>
      </div>
      <div style={{ fontSize:12.5, color:C.textMuted, marginBottom:12, lineHeight:1.4 }}>{desc}</div>
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => onChange(parseFloat(e.target.value))} style={{ width:"100%", accentColor:C.green }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.textMuted, marginTop:2 }}><span>{lo}</span><span>{hi}</span></div>
    </div>
  );
}
function Switch({ label, sub, on, onChange }) {
  const C = useTheme();
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, justifyContent:"space-between" }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:15, fontWeight:500 }}>{label}</div>
        {sub && <div style={{ fontSize:12.5, color:C.textMuted, marginTop:3, lineHeight:1.4 }}>{sub}</div>}
      </div>
      <span onClick={() => onChange(!on)} style={{ width:46, height:28, borderRadius:999, background:on?C.green:C.border, position:"relative", flexShrink:0, cursor:"pointer", transition:"background 140ms" }}>
        <span style={{ position:"absolute", top:3, left:on?21:3, width:22, height:22, borderRadius:"50%", background:on?C.bg:C.textMuted, transition:"left 140ms" }} />
      </span>
    </div>
  );
}
function TimeField({ label, value, onChange }) {
  const C = useTheme();
  const { mode } = useThemeMode();
  return (
    <div style={{ flex:1 }}>
      <div style={{ fontSize:12, color:C.textMuted, marginBottom:5 }}>{label}</div>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)} style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, color:C.text, fontSize:15, padding:"10px 12px", colorScheme:mode }} />
    </div>
  );
}
function Spinner() {
  const C = useTheme();
  return (
    <div style={{ display:"flex", justifyContent:"center", padding:"40px 0" }}>
      <div style={{ width:28, height:28, border:`2px solid ${C.border}`, borderTopColor:C.green, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
    </div>
  );
}
function RankChips({ rankCtl }) {
  const C = useTheme();
  const { topN, setTopN } = rankCtl;
  const isCustom = !RANK_CHIPS.includes(topN);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginTop:12 }}>
      <span style={{ fontSize:11.5, color:C.textMuted, marginRight:2, letterSpacing:0.3 }}>RANK</span>
      {RANK_CHIPS.map((n) => {
        const on = topN === n;
        return (
          <button key={n} onClick={() => setTopN(n)} style={{ padding:"5px 11px", borderRadius:999, fontSize:12.5, fontWeight:600, cursor:"pointer", border:`1px solid ${on?C.green+"66":C.border}`, background:on?C.greenDim:"transparent", color:on?C.green:C.textMuted }}>
            {n === RANK_MAX ? "All" : n}
          </button>
        );
      })}
      {isCustom && <span style={{ padding:"5px 11px", borderRadius:999, fontSize:12.5, fontWeight:600, border:`1px solid ${C.green}66`, background:C.greenDim, color:C.green }}>Top {topN}</span>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// TAB BAR
// ════════════════════════════════════════════════════════════════════
function TabBar({ active, onChange, unread }) {
  const C = useTheme();
  const tabs = [["alerts","Alerts","◎"],["whale","Whale","◆"],["dormant","Dormant","☾"],["leaderboard","Leaders","≡"],["favorites","Favorites","★"],["settings","Settings","⚙"]];
  return (
    <div style={{ position:"sticky", bottom:0, display:"flex", borderTop:`1px solid ${C.border}`, background:`${C.navBg}f2`, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}>
      {tabs.map(([k, label, icon]) => {
        const on = active === k;
        return (
          <button key={k} onClick={() => onChange(k)} style={{ flex:1, padding:"10px 0 14px", background:"transparent", border:"none", cursor:"pointer", color:on?C.navText:C.navMuted, display:"flex", flexDirection:"column", alignItems:"center", gap:3, position:"relative" }}>
            <span style={{ fontSize:18, lineHeight:1 }}>{icon}</span>
            <span style={{ fontSize:11, fontWeight:on?600:500 }}>{label}</span>
            {k==="alerts" && unread > 0 && <span style={{ position:"absolute", top:6, right:"calc(50% - 18px)", minWidth:7, height:7, borderRadius:999, background:C.navText, boxShadow:`0 0 6px ${C.navText}` }} />}
          </button>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ROOT
// ════════════════════════════════════════════════════════════════════
function AppInner() {
  const C = useTheme();
  // Three checks, not one — a RETURNING user (ftl_onboarded already set from
  // a prior session) must still be routed through the walkthrough if they
  // haven't seen it yet. completeOnboarding() only covers a brand-new
  // session's onboarding→priming→walkthrough chain; without this initializer
  // also checking ftl_walkthrough_seen, a returning already-onboarded user
  // would jump straight to "app" and never see it (same bug class as
  // apps/mobile/app/index.tsx's routing gate — fixed there for the same reason).
  const [phase,   setPhase]   = useState(() => {
    if (!localStorage.getItem("ftl_onboarded")) return "onboarding";
    if (!localStorage.getItem("ftl_walkthrough_seen")) return "walkthrough";
    return "app";
  });
  const [tab,     setTab]     = useState("alerts");
  const [detail,  setDetail]  = useState(null);
  const [favSet,  setFavSet]  = useState(() => new Set());
  const [traders, setTraders] = useState(MOCK_TRADERS); // shared trader list for Favorites tab

  // Lifted settings — single source of truth read by AlertsTab for filtering
  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);

  // Rank filter
  const [topN,         setTopN]         = useState(RANK_MAX);
  const [favBypassRank, setFavBypassRank] = useState(true);
  const [rankWindow,   setRankWindow]   = useState("day");
  const rankCtl = { topN, setTopN, favBypassRank, setFavBypassRank, rankWindow, setRankWindow };

  // Lifted read-state — AlertsTab writes here, TabBar reads here for the badge dot
  const [readSet,    setReadSet]    = useState(() => new Set());
  const [unread,     setUnread]     = useState(0);
  const markRead    = (h)    => setReadSet((p) => new Set(p).add(h));
  const markAllRead = (list) => setReadSet((p) => { const n = new Set(p); list.forEach((h) => n.add(h)); return n; });
  const reportUnread = useCallback((count) => setUnread(count), []);

  // On mount: register device + load server settings
  useEffect(() => {
    registerDevice();
    fetchSettings(DEVICE_ID).then(({ data }) => {
      if (!data) return;
      setSettings({
        conviction: data.conviction_multiplier ?? SETTINGS_DEFAULTS.conviction,
        winFloor:   data.win_rate_floor        ?? SETTINGS_DEFAULTS.winFloor,
        favBypass:  data.fav_bypass_filters    ?? SETTINGS_DEFAULTS.favBypass,
        notif:      data.notif_enabled         ?? SETTINGS_DEFAULTS.notif,
        quiet:      !!(data.quiet_from),
        from:       data.quiet_from            ?? SETTINGS_DEFAULTS.from,
        to:         data.quiet_to              ?? SETTINGS_DEFAULTS.to,
        whaleUsd:     data.whale_usd_threshold     ?? SETTINGS_DEFAULTS.whaleUsd,
        dormancyDays: data.dormancy_threshold_days ?? SETTINGS_DEFAULTS.dormancyDays,
        excludeWhale:   (data.excluded_categories ?? "").split(",").includes("whale"),
        excludeDormant: (data.excluded_categories ?? "").split(",").includes("dormant_wallet"),
      });
      if (data.top_n)          setTopN(data.top_n);
      if (data.fav_bypass_rank != null) setFavBypassRank(data.fav_bypass_rank);
      if (data.rank_window)    setRankWindow(data.rank_window);
    });
    // Load leaderboard for Favorites tab trader metadata
    fetchLeaderboard("day").then(({ data }) => setTraders(data));
  }, []);

  const toggleFav   = (id) => setFavSet((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const openTrader  = (t)  => setDetail(t);
  const completeOnboarding = () => {
    localStorage.setItem("ftl_onboarded","1");
    setPhase(localStorage.getItem("ftl_walkthrough_seen") ? "app" : "walkthrough");
  };
  const completeWalkthrough = () => { localStorage.setItem("ftl_walkthrough_seen","1"); setPhase("app"); };

  // `unread` is now real state — lifted from AlertsTab via reportUnread callback.

  // Sample alert for Priming screen (use first mock so it always looks good)
  const sampleAlert = normaliseAlert(MOCK_ALERTS[0]);

  let body;
  if      (phase === "onboarding") body = <Onboarding onContinue={() => setPhase("priming")} />;
  else if (phase === "priming")    body = <Priming onEnable={completeOnboarding} onSkip={completeOnboarding} sampleAlert={sampleAlert} />;
  else if (phase === "walkthrough") body = <Walkthrough onDone={completeWalkthrough} sampleAlert={sampleAlert} />;
  else if (detail)                 body = <TraderDetail trader={detail} favSet={favSet} toggleFav={toggleFav} onBack={() => setDetail(null)} settings={settings} />;
  else if (tab === "alerts")       body = <AlertsTab favSet={favSet} rankCtl={rankCtl} settings={settings} readSet={readSet} markRead={markRead} markAllRead={markAllRead} onUnreadChange={reportUnread} />;
  else if (tab === "whale")        body = <CategoryAlertsTab category="whale" title="Whale" emptyText="No whale-sized trades yet. Trades over the whale threshold from any tracked trader show up here, regardless of your regular alert settings." favSet={favSet} readSet={readSet} markRead={markRead} markAllRead={markAllRead} />;
  else if (tab === "dormant")      body = <CategoryAlertsTab category="dormant_wallet" title="Dormant Wallet" emptyText="No dormant-wallet reactivations yet. Trades from wallets that had gone quiet before suddenly trading again show up here, regardless of your regular alert settings." favSet={favSet} readSet={readSet} markRead={markRead} markAllRead={markAllRead} />;
  else if (tab === "leaderboard")  body = <LeaderboardTab favSet={favSet} toggleFav={toggleFav} onOpen={openTrader} rankCtl={rankCtl} />;
  else if (tab === "favorites")    body = <FavoritesTab favSet={favSet} toggleFav={toggleFav} onOpen={openTrader} traders={traders} />;
  else                             body = <SettingsTab favSet={favSet} toggleFav={toggleFav} rankCtl={rankCtl} settings={settings} setSettings={setSettings} traders={traders} onReplayWalkthrough={() => setPhase("walkthrough")} />;

  return (
    <div style={{ background:C.bg, color:C.text, fontFamily:FONT, minHeight:"100vh", display:"flex", flexDirection:"column" }}>
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
        <div style={{ flex:1 }}>{body}</div>
      </div>
      {phase === "app" && <TabBar active={tab} onChange={(k) => { setDetail(null); setTab(k); }} unread={unread} />}
      <style>{`
        @keyframes ftlp { 0%{transform:scale(1);opacity:.7} 70%{transform:scale(1.5);opacity:0} 100%{opacity:0} }
        @keyframes spin  { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { [style*="ftlp"],[style*="spin"] { animation:none !important; } }
        button:focus-visible,a:focus-visible,input:focus-visible,[role="button"]:focus-visible { outline:2px solid ${C.green}; outline-offset:2px; }
        input[type=range] { height:4px; }
      `}</style>
    </div>
  );
}

// Not wrapped in ThemeProvider here — main.jsx wraps it (and the sibling
// SupportWidget) in one shared provider so both stay in sync.
export default function App() {
  return <AppInner />;
}
